
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Home, Zap, DollarSign } from 'lucide-react';
import { ChargingStation } from '@/types/task';
import { initialStations } from '@/data/stations';

const HostCharger = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    chargerType: 'standard',
    powerOutput: 7,
    price: 0.30,
    amenities: '',
    availableHours: '9:00 AM - 9:00 PM',
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const newStation: ChargingStation = {
      id: uuidv4(),
      title: formData.title,
      location: 'Home Charger',
      address: formData.address,
      price: parseFloat(formData.price.toString()),
      pricePerKwh: parseFloat(formData.price.toString()),
      rating: 0,
      available: true,
      chargerType: formData.chargerType as 'fast' | 'standard' | 'ultra-fast',
      powerOutput: parseFloat(formData.powerOutput.toString()),
      hostName: 'Your Name', // In a real app, this would be the logged-in user's name
      isHomeCharger: true,
      availableHours: formData.availableHours,
      amenities: formData.amenities.split(',').map(item => item.trim()),
      photos: ['https://images.unsplash.com/photo-1647349277782-9e36cf1ab402?q=80&w=2070&auto=format&fit=crop'],
      images: ['https://images.unsplash.com/photo-1647349277782-9e36cf1ab402?q=80&w=2070&auto=format&fit=crop'],
      coordinates: {
        latitude: 37.7749, // In a real app, this would be geocoded from the address
        longitude: -122.4194
      },
      // Add required fields
      plugTypes: ['Type 2'],
      numberOfPorts: 1,
      paymentTypes: ['Credit Card'],
      checkins: 0,
      status: 'operational',
      verified: false,
      networkOperator: 'Private',
      reviews: [],
      description: formData.description,
    };
    
    // For demo purposes, we're just adding to the existing stations array
    // In a real app, this would be saved to a database
    initialStations.push(newStation);
    
    toast({
      title: 'Charger Registered Successfully',
      description: 'Your home EV charger has been listed for others to use.',
    });
    
    navigate('/');
  };

  return (
    <div className="container max-w-3xl py-6 px-4 md:px-6">
      <Button 
        variant="ghost" 
        className="mb-6 pl-0" 
        onClick={() => navigate('/')}
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to home
      </Button>
      
      <Card>
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl">Host Your EV Charger</CardTitle>
          <CardDescription>
            Share your home charger with other EV owners and earn money when they use it.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="title">Listing Title</Label>
                <Input 
                  id="title"
                  name="title"
                  placeholder="e.g. Convenient Home Charger in Downtown"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="address">Full Address</Label>
                <Input 
                  id="address"
                  name="address"
                  placeholder="123 Main St, City, State, ZIP"
                  value={formData.address}
                  onChange={handleChange}
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Your exact address will only be shared after a booking is confirmed.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="chargerType">Charger Type</Label>
                  <Select 
                    name="chargerType" 
                    value={formData.chargerType}
                    onValueChange={(value) => handleSelectChange('chargerType', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select charger type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard (Level 2)</SelectItem>
                      <SelectItem value="fast">Fast Charging</SelectItem>
                      <SelectItem value="ultra-fast">Ultra-Fast</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="powerOutput">Power Output (kW)</Label>
                  <Input 
                    id="powerOutput"
                    name="powerOutput"
                    type="number"
                    min="3"
                    max="350"
                    step="0.1"
                    value={formData.powerOutput}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="price">Price per kWh ($)</Label>
                <Input 
                  id="price"
                  name="price"
                  type="number"
                  min="0.1"
                  max="2"
                  step="0.01"
                  value={formData.price}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="availableHours">Available Hours</Label>
                <Input 
                  id="availableHours"
                  name="availableHours"
                  placeholder="e.g. 9:00 AM - 9:00 PM"
                  value={formData.availableHours}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="amenities">Amenities (comma separated)</Label>
                <Input 
                  id="amenities"
                  name="amenities"
                  placeholder="e.g. WiFi, Restroom, Coffee, Parking"
                  value={formData.amenities}
                  onChange={handleChange}
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description"
                  name="description"
                  placeholder="Tell potential users about your charger setup and location..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>
          
            <CardFooter className="px-0 pt-6">
              <Button type="submit" className="w-full">
                Register Your Charger
              </Button>
            </CardFooter>
          </form>
        </CardContent>
      </Card>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Home className="h-8 w-8 text-blue-500 mb-3" />
              <h3 className="font-semibold mb-2">Share Your Charger</h3>
              <p className="text-sm text-muted-foreground">
                List your home EV charger when you're not using it
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <DollarSign className="h-8 w-8 text-green-500 mb-3" />
              <h3 className="font-semibold mb-2">Earn Money</h3>
              <p className="text-sm text-muted-foreground">
                Set your own rates and earn when others charge
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-slate-50">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Zap className="h-8 w-8 text-yellow-500 mb-3" />
              <h3 className="font-semibold mb-2">Help The Community</h3>
              <p className="text-sm text-muted-foreground">
                Support EV adoption by expanding charging options
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default HostCharger;
